import {
  Button,
  HStack,
  Icon,
  IconButton,
  ModalCloseButton,
  Spinner,
  Stack,
  Tooltip,
} from '@chakra-ui/react';
import { useUIStore } from '~/store';
import { Filters } from './search/Filters';
import { Categories } from './search/Categories';
import { Statuses } from './search/Statuses';
import { useUser } from '~/hooks/useUser';
import { useFilters } from '~/hooks/useFilters';
import { shallow } from 'zustand/shallow';
import { useHomesStore } from '~/store/useHomesStore';
import { useTranslations } from 'next-intl';
import { CountResult, SearchTab } from '@habity/types';
import { ICONS } from '~/constants';
import { useUserAction } from '~/hooks/useUserAction';
import { useSearchActionRequirements } from '~/hooks/useSearchActionRequirements';

function getButtonLabel(tab: SearchTab, count: CountResult) {
  const buttonCount = getButtonCount(tab, count);

  if (buttonCount === 0) {
    return 'Inga resultat';
  }

  let label = '';

  if (tab === 'homes') {
    label += `Visa ${buttonCount} bostäder`;
  } else if (tab === 'speculators') {
    label += `Visa ${buttonCount} spekulanter`;
  } else if (tab === 'brokers') {
    label += `Visa ${buttonCount} mäklare`;
  } else if (tab === 'projects') {
    label += `Visa ${buttonCount} projekt`;
  } else if (tab === 'places') {
    label += `Visa ${buttonCount} kontor`;
  }

  return label;
}

const getButtonCount = (tab: SearchTab, count: CountResult) => {
  if (tab === 'homes') {
    return count.homes;
  } else if (tab === 'speculators') {
    // Use unique count for the filter button display
    return count.speculatorsUnique || count.speculators;
  } else if (tab === 'brokers') {
    return count.brokers;
  } else if (tab === 'projects') {
    return count.projects;
  } else if (tab === 'places') {
    return count.places;
  }
};

interface SearchFormProps {
  onClose: () => void;
  hideCategories?: boolean;
  hidePersonas?: boolean;
  tab: SearchTab;
}

export function SearchForm({
  onClose,
  hideCategories: hideSearchCategories,
  tab,
}: SearchFormProps) {
  const { user } = useUser();
  const count = useHomesStore((state) => state.count);
  const { loading } = useUIStore(
    (state) => ({ loading: state.loading }),
    shallow
  );
  const { isLoading, homesLoading, staticHomesLoading } = loading;

  const t = useTranslations('Forms.Search');

  const { filters, hasFilters, resetFilters } = useFilters();
  const { executeAction, isLoading: isExecutingAction } = useUserAction();
  const { canExecute: canCreateSearch, unmetMessage } =
    useSearchActionRequirements();

  return (
    <Stack
      spacing="4"
      display="flex"
      maxW="100vw"
      maxH={{ mobile: '80vh', desktop: '90vh' }}
      overflowX="hidden"
      position="relative"
    >
      <HStack
        px="4"
        spacing="2"
        justifyContent="flex-end"
        pt="2"
        pb="0"
        position="sticky"
        top="0"
        backgroundColor="white"
        opacity="1"
        zIndex="10"
      >
        <IconButton
          top="0"
          right="0"
          as={ModalCloseButton}
          size="lg"
          aria-label="Stäng"
          icon={<Icon as={ICONS.CLOSE} boxSize="24px" />}
          variant="secondary"
          _hover={{
            backgroundColor: 'blackAlpha.200',
          }}
          onClick={onClose}
        />
      </HStack>

      {/* Filters */}
      <Stack spacing="3" h="auto" px="4" mt="-2">
        {!hideSearchCategories && <Categories />}
        <Filters
          hideCategories={!hideSearchCategories}
          hideBedrooms={tab === 'speculators'}
          hideBuildYear={tab === 'speculators'}
          hideMonthlyFee={tab === 'speculators'}
          hideNearby={tab === 'speculators'}
          hideFeatures={tab === 'speculators'}
          hideStatuses
        />
        {tab !== 'speculators' && <Statuses signedIn={!!user} />}
        {hasFilters() && (
          <Button
            variant="ghost"
            size="xs"
            fontWeight="normal"
            color="blackAlpha.500"
            _hover={{ color: 'blackAlpha.700' }}
            onClick={() => resetFilters()}
            alignSelf="center"
            mt="2"
            leftIcon={<Icon as={ICONS.CLOSE} boxSize="14px" />}
          >
            Återställ filter
          </Button>
        )}
      </Stack>

      {/* Action buttons */}
      <Stack
        position="sticky"
        bottom="0"
        spacing="0"
        align="center"
        backgroundColor="white"
        py="4"
      >
        <HStack
          flex="1"
          w="100%"
          px={{ mobile: '4', desktop: '0' }}
          maxW={{ desktop: '400px' }}
        >
          <Button
            flex="1"
            isLoading={isLoading || homesLoading || staticHomesLoading}
            loadingText={t('Buttons.loading')}
            onClick={() => {
              onClose();
            }}
            type="submit"
            fontSize="sm"
          >
            {getButtonLabel(tab, count)}
          </Button>
          {tab === 'homes' && (
            <Tooltip
              label={unmetMessage}
              isDisabled={canCreateSearch}
              shouldWrapChildren
              hasArrow
              placement="top"
            >
              <Button
                flex="1"
                variant={{ mobile: 'secondary', desktop: 'outline' }}
                fontSize="sm"
                onClick={() => {
                  executeAction({
                    action: 'created_search_profile',
                    searchFilters: filters || undefined,
                  });
                }}
                leftIcon={
                  isExecutingAction ? (
                    <Spinner size="sm" />
                  ) : (
                    <Icon as={ICONS.HOMEBUYERS_ALERT} />
                  )
                }
                isDisabled={!canCreateSearch || isExecutingAction}
              >
                {isExecutingAction ? 'Bevakar...' : 'Bevaka sökning'}
              </Button>
            </Tooltip>
          )}
        </HStack>
      </Stack>
    </Stack>
  );
}
